import React, { FunctionComponent, useState } from 'react';
import { RichText, DateField } from '@sitecore-jss/sitecore-jss-react';
import { ContentHubSpecialOffersSlideModel } from '../models/fields.model';
import RoutableNotEditableLink from '../../@shared/routableNotEditableLink';
import CTA from '../../@shared/CTA';
import ctaTypes from '../../../enums/CTA.enums';
import Icon from '../../@shared/icon';
import Picture from '../../@shared/picture';
import { iconSize } from '../../../constants/icon.constants';
import { targetLinkdsk } from '../../../utils/targetDskLink';
import useMediaQuery from '../../../hooks/useMediaQuery';
import breakpoints from '../../../constants/breakpoints.constants';
import { googleCtaClickEvent, googleSpecialOffersClick } from '../../../utils/analytics';
import { lineSpecialOffersClick, primaryCTA, secondaryCTA, seriesSpecialOffersClick } from '../../../constants/analytics.constants';
import useSitecoreContext from '../../../hooks/useSitecoreContext';
import DictionaryTerm from '../../@shared/dictionaryTerm';
import ModalDialog from '../../@shared/modalDialog';
import useFormatDate from '../../../hooks/useFormatDate';

const CarouselSlide: FunctionComponent<{
  model: ContentHubSpecialOffersSlideModel;
  isEE: boolean;
  isSeries: boolean;
  iconImage: string;
}> = ({ model, isEE, iconImage, isSeries }) => {
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints['tbl-p']}px)`);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const buttonPrimary = {
    value: {
      href: model?.buttonPrimary?.value?.href,
      text: model?.buttonPrimary?.value?.title,
      target: model?.buttonPrimary?.value?.target,
    },
  };
  const buttonSecondary = {
    value: {
      href: model?.buttonSecondary?.value?.href,
      text: model?.buttonSecondary?.value?.title,
      target: model?.buttonSecondary?.value?.target,
    },
  };
  const hideButtonWrapper =
    !model.buttonPrimary?.value.href &&
    !model.buttonPrimary?.value.title &&
    !model.buttonSecondary?.value.href &&
    !model.buttonSecondary?.value.title;

  const { sitecoreContext } = useSitecoreContext();
  const formattedDate = useFormatDate(model.expiration.value);
  const productLine = sitecoreContext?.analytics?.['productLine'] ? sitecoreContext?.analytics?.['productLine'] : undefined;
  const productSeries = sitecoreContext?.analytics?.['productSeries'] ? sitecoreContext?.analytics?.['productSeries'] : undefined;

  return (
    <>
      {model.disclaimer.value && (
        <ModalDialog isOpen={isModelOpen} close={() => setIsModelOpen(false)} className="special-offer-detail-modal">
          <RichText editable={false} tag="div" field={model.disclaimer} className="rich-text special-offer-detail-modal__content" />
        </ModalDialog>
      )}
      <div className={`special-offers-card special-offers-card--${model.cardStyles.value === 'Wide' ? 'large' : 'small'}`}>
        <article className="card-with-header-image slide-card slide-card--strech">
          <Picture
            productImageProps={{
              ...model.productImageProps,
              imageClassName: 'slide-card__image slide-card__image--no-margin-bottom',
            }}
          />
          <div className="special-offers-card__body">
            <div className="special-offers-card__title">
              {model.title && (
                <RichText
                  tag="span"
                  field={model.title}
                  className="slide-card__text slide-card__text--inline slide-card__text--overflow-unset rich-text"
                />
              )}
            </div>
            {model.description && <RichText tag="div" field={model.description} className="special-offers-card__description rich-text" />}
          </div>
          {model.disclaimer.value && (
            <button onClick={() => setIsModelOpen(true)} className="special-offers-card__disclaimer">
              <DictionaryTerm keyword={'cnhi-special-offers-disclaimer'} />
            </button>
          )}
          {model.expiration.value && (
            <p className="special-offers-card__expiry">
              <DictionaryTerm keyword={'cnhi-special-offers-expiration-label'} />
              :&nbsp;
              <DateField editable={false} field={model.expiration} tag="span" render={() => formattedDate} />
            </p>
          )}
          {!hideButtonWrapper && (
            <div className="special-offers-card__cta-button-container">
              {(isEE || model.buttonPrimary?.value.href || model.buttonPrimary?.value.title) && (
                <CTA
                  type={ctaTypes.primaryOrange}
                  extraClassName={'slide-card__button slide-card__button--primary'}
                  field={{
                    value: {
                      href: model.buttonPrimary?.value?.href,
                      text: model.buttonPrimary?.value?.title,
                      target: model?.buttonPrimary?.value?.target,
                    },
                  }}
                  onClick={() => {
                    const event = isSeries ? seriesSpecialOffersClick : lineSpecialOffersClick;
                    const linkText = buttonPrimary?.value?.text;
                    const linkUrl = buttonPrimary?.value?.href;
                    googleCtaClickEvent(primaryCTA, linkUrl, linkText, '');
                    googleSpecialOffersClick(event, model.title.value, productLine, productSeries, linkText, linkUrl);
                  }
                  }
                  target={targetLinkdsk(buttonPrimary, isDesktop)}
                />
              )}
              {(isEE || (model.buttonSecondary?.value.href && model.buttonSecondary?.value.title)) && model.cardStyles.value === 'Wide' && (
                <div className="slide-card__button slide-card__button--secondary">
                  <span className="link-with-arrow">
                    <RoutableNotEditableLink
                      href={model.buttonSecondary.value.href}
                      target={targetLinkdsk(buttonSecondary, isDesktop)}
                      onClick={() => {
                        const linkText = buttonSecondary?.value?.text;
                        const linkUrl = buttonSecondary?.value?.href;
                        googleCtaClickEvent(secondaryCTA, linkUrl, linkText, '');
                      }}
                    >
                      {model.buttonSecondary.value.title}
                    </RoutableNotEditableLink>
                    <Icon iconName={iconImage} size={iconSize.dsk} color="primary" />
                  </span>
                </div>
              )}
            </div>
          )}
        </article>
      </div>
    </>
  );
};

export default CarouselSlide;
